import './styles.scss'
import { Button } from 'primereact/button'
import React, { useRef, useState, useEffect } from 'react'
import ImageCropper from 'Components/UIComponent/ImageCropper'
import MentionDropdown from 'Screens/CoffeeWebSocial/Components/CoffeeCommunities/Components/MentionDropdown'
import { COFFEE_COMMUNITIES_METHODS } from 'Screens/CoffeeWebSocial/Components/CoffeeCommunities/Utils'
import REG_EX from 'Utils/Constants/RegEx'

export default function AttachmentPreview({ attachments = [], scrollToBottom, handleMediaSelected, handleDocumentSelected, onAddAttachment, removeAttachment, onUpdateAttachment, onCloseAll, onSendAttachments, isUploadingImage, setAttachments, addFiles: addFilesProp, isInternalCommunity = false, mentionMembers = [] }) {
  const fileInputRef = useRef(null)
  const captionTextareaRef = useRef(null)
  const captionRowRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [showCropper, setShowCropper] = useState(false)
  const [croppingIndex, setCroppingIndex] = useState(null)
  const [isAnnotating, setIsAnnotating] = useState(false)
  const [brushColor, setBrushColor] = useState('#00a884') // WhatsApp green
  const [brushSize, setBrushSize] = useState(4)
  const [strokesById, setStrokesById] = useState(() => new Map()) // { [attachmentId]: { strokes: [...], redo: [...] } }
  const [appliedStrokesById, setAppliedStrokesById] = useState(() => new Map()) // { [attachmentId]: { originalImageUrl, appliedStrokes: [stroke, stroke, ...] } }
  const canvasRef = useRef(null)
  const imgElementRef = useRef(null)
  const isDrawingRef = useRef(false)
  const lastPointRef = useRef(null)
  const scaleRef = useRef({ x: 1, y: 1 })
  const brushColorRef = useRef('#00a884')
  const brushSizeRef = useRef(4)

  const activeIdRef = useRef(null)
  const isAnnotatingRef = useRef(false)
  const pendingPasteCountRef = useRef(0) // Track number of images being pasted
  const prevAttachmentsLengthRef = useRef(0) // Track previous attachments length

  const { textMethods } = COFFEE_COMMUNITIES_METHODS

  const getActiveStrokeState = (id) => {
    const key = id || active?.id || activeIdRef.current

    return strokesById.get(key) || { strokes: [], redo: [] }
  }
  const getActiveStrokes = () => getActiveStrokeState().strokes || []
  const getActiveRedo = () => getActiveStrokeState().redo || []

  const getAppliedStrokes = (id) => {
    const key = id || active?._undoKey || active?.id || activeIdRef.current

    return appliedStrokesById.get(key)
  }

  // local captions map used as fallback if parent doesn't provide onUpdateAttachment
  const [localCaptions, setLocalCaptions] = useState(() => new Map())

  // caption @ mention dropdown (internal communities only)
  const [showCaptionMentionDropdown, setShowCaptionMentionDropdown] = useState(false)
  const [captionMentionSearchTerm, setCaptionMentionSearchTerm] = useState('')
  const [captionSelectedMentionIndex, setCaptionSelectedMentionIndex] = useState(0)
  const [captionMentionStartPosition, setCaptionMentionStartPosition] = useState(null)

  // active attachment shortcuts (placed early to avoid temporal dead zone)
  const active = attachments[activeIndex] || null
  const isImage = active?.mime?.startsWith?.('image/')
  const isVideo = active?.mime?.startsWith?.('video/')

  // Initialize prevAttachmentsLengthRef on mount or when attachments first load
  useEffect(() => {
    if (prevAttachmentsLengthRef.current === 0 && attachments.length > 0) {
      prevAttachmentsLengthRef.current = attachments.length
    } else if (attachments.length === 0) {
      prevAttachmentsLengthRef.current = 0
    }
  }, [attachments.length])

  // keep activeIndex valid when attachments change

  useEffect(() => {
    if (attachments.length === 0) {
      setActiveIndex(0)
      setIsAnnotating(false)
    } else if (activeIndex >= attachments.length) {
      setActiveIndex(attachments.length - 1)
    }

    // keep localCaptions entries in sync for new attachments
    setLocalCaptions((prev) => {
      const next = new Map()

      attachments.forEach((att) => {
        next.set(att.id, att.caption ?? prev.get(att.id) ?? '')
      })

      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments])

  // Handle switching to newly pasted images and clearing their captions
  useEffect(() => {
    const currentLength = attachments.length
    const prevLength = prevAttachmentsLengthRef.current

    // Only handle if attachments were added (not removed) and we have a pending paste
    if (pendingPasteCountRef.current > 0 && currentLength > prevLength && currentLength > 0) {
      // Set active index to the last newly pasted image (the last one in the array)
      const newActiveIndex = currentLength - 1

      setActiveIndex(newActiveIndex)

      // Clear caption for the newly active attachment
      const newActive = attachments[newActiveIndex]

      if (newActive?.id) {
        updateCaptionLocal(newActive.id, '')
      }

      // Reset the pending paste count
      pendingPasteCountRef.current = 0

      // Maintain focus on textarea after switching (like WhatsApp)
      requestAnimationFrame(() => {
        if (captionTextareaRef.current) {
          captionTextareaRef.current.focus()
        }
      })

      setTimeout(() => {
        if (captionTextareaRef.current) {
          captionTextareaRef.current.focus()
        }
      }, 0)
    }

    // Update the previous length ref
    prevAttachmentsLengthRef.current = currentLength
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments.length])

  useEffect(() => {
    activeIdRef.current = active?.id || null
  }, [active?.id])

  useEffect(() => {
    isAnnotatingRef.current = isAnnotating
  }, [isAnnotating])

  useEffect(() => {
    activeIdRef.current = active?.id || null
  }, [active?.id])

  useEffect(() => {
    isAnnotatingRef.current = isAnnotating
  }, [isAnnotating])

  const COLORS = ['#00a884', '#111b21', '#f44336', '#ff9800', '#2196f3', '#9c27b0']

  const resetCanvas = (img) => {
    const canvas = canvasRef.current

    if (!canvas || !img) return

    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight

    // scale factors for pointer coordinate mapping
    const rect = canvas.getBoundingClientRect()

    scaleRef.current = {
      x: canvas.width / rect.width,
      y: canvas.height / rect.height
    }

    const ctx = canvas.getContext('2d')

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

    getActiveStrokes().forEach((stroke) => drawStroke(ctx, stroke))
  }

  const drawStroke = (ctx, stroke) => {
    if (!stroke?.points?.length) return

    ctx.save()
    ctx.lineWidth = stroke.size
    ctx.strokeStyle = stroke.color
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(stroke.points[0].x, stroke.points[0].y)
    for (let i = 1; i < stroke.points.length; i += 1) {
      const p = stroke.points[i]

      ctx.lineTo(p.x, p.y)
    }
    ctx.stroke()
    ctx.restore()
  }

  const redrawCanvas = () => {
    const canvas = canvasRef.current
    const img = imgElementRef.current

    if (!canvas || !img) return
    const ctx = canvas.getContext('2d')

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    getActiveStrokes().forEach((stroke) => drawStroke(ctx, stroke))
  }

  const redrawWithStrokes = (strokes) => {
    const canvas = canvasRef.current
    const img = imgElementRef.current

    if (!canvas || !img) return
    const ctx = canvas.getContext('2d')

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx
      .drawImage(
        img,
        0,
        0,
        canvas.width,
        canvas.height
      )(strokes || [])
      .forEach((stroke) => drawStroke(ctx, stroke))
  }

  const pointerToCanvas = (event) => {
    const canvas = canvasRef.current

    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const x = (event.clientX - rect.left) * scaleRef.current.x
    const y = (event.clientY - rect.top) * scaleRef.current.y

    return { x, y }
  }

  const handleMouseDown = (e) => {
    if (!isAnnotatingRef.current) return
    const point = pointerToCanvas(e)

    if (!point) return

    isDrawingRef.current = true
    lastPointRef.current = point

    const stroke = {
      id: `stroke-${Date.now()}`,
      color: brushColorRef.current,
      size: brushSizeRef.current,
      points: [point]
    }

    setStrokesById((prev) => {
      const map = new Map(prev)
      const key = active?.id || activeIdRef.current

      if (!key) return prev
      const existing = map.get(key) || { strokes: [], redo: [] }

      map.set(key, { ...existing, strokes: [...existing.strokes, stroke], redo: [] })

      return map
    })
  }

  const handleMouseMove = (e) => {
    if (!isAnnotatingRef.current || !isDrawingRef.current) return
    const point = pointerToCanvas(e)

    if (!point || !lastPointRef.current) return

    // append to latest stroke
    setStrokesById((prev) => {
      const map = new Map(prev)
      const key = active?.id || activeIdRef.current

      if (!key) return prev
      const existing = map.get(key) || { strokes: [], redo: [] }

      if (!existing.strokes.length) return prev

      const updatedStrokes = [...existing.strokes]
      const currentStroke = { ...updatedStrokes[updatedStrokes.length - 1] }

      currentStroke.points = [...currentStroke.points, point]
      updatedStrokes[updatedStrokes.length - 1] = currentStroke
      map.set(key, { ...existing, strokes: updatedStrokes })

      return map
    })

    // draw incremental segment for smoothness
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')

    if (ctx) {
      ctx.save()
      ctx.lineWidth = brushSizeRef.current
      ctx.strokeStyle = brushColorRef.current
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y)
      ctx.lineTo(point.x, point.y)
      ctx.stroke()
      ctx.restore()
    }

    lastPointRef.current = point
  }

  const handleMouseUp = () => {
    if (!isAnnotatingRef.current) return
    isDrawingRef.current = false
    lastPointRef.current = null
  }

  useEffect(() => {
    if (!isAnnotating) return undefined
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isAnnotating, handleMouseMove])

  useEffect(() => {
    // exit annotation/crop when switching attachments
    setIsAnnotating(false)
    setShowCropper(false)
  }, [activeIndex])

  // Adjust textarea height when caption value changes or active attachment changes
  useEffect(() => {
    if (captionTextareaRef.current && active?.id) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        adjustTextareaHeight(captionTextareaRef.current)
      })
    }
  }, [active?.id, active?.caption, attachments])

  // Keep refs in sync with state for event handlers
  useEffect(() => {
    brushColorRef.current = brushColor
  }, [brushColor])

  useEffect(() => {
    brushSizeRef.current = brushSize
  }, [brushSize])

  // Initialize canvas whenever we enter annotation or active image changes
  useEffect(() => {
    if (!isAnnotating || !isImage) return
    const img = new Image()
    const currentId = active?.id

    img.src = active.previewUrl
    img.onload = () => {
      imgElementRef.current = img
      // Ensure activeIdRef is set before resetting canvas
      if (currentId) {
        activeIdRef.current = currentId
      }
      resetCanvas(img)
    }
  }, [isAnnotating, active?.id, active?.previewUrl, isImage])

  const startAnnotating = () => {
    if (!isImage) return
    setShowCropper(false)
    // Ensure activeIdRef is set immediately
    if (active?.id) {
      activeIdRef.current = active.id
    }
    setIsAnnotating(true)
  }

  const stopAnnotating = () => {
    setIsAnnotating(false)
    isDrawingRef.current = false
    lastPointRef.current = null
  }

  const clearActiveAnnotations = () => {
    // remove strokes for the current attachment
    setStrokesById((prev) => {
      const key = activeIdRef.current

      if (!key || !prev.has(key)) return prev
      const next = new Map(prev)

      next.delete(key)

      return next
    })

    // redraw base image only (remove any drawn strokes from canvas preview)
    if (imgElementRef.current && canvasRef.current) {
      const img = imgElementRef.current

      resetCanvas(img)
    }
  }

  const handleCancelAnnotate = () => {
    clearActiveAnnotations()
    stopAnnotating()
  }

  const handleUndo = () => {
    const key = active?._undoKey || active?.id || activeIdRef.current

    if (!key) return

    const currentStrokes = getActiveStrokes()
    const appliedData = getAppliedStrokes(key)

    if (currentStrokes.length > 0) {
      let updatedStrokes = null

      setStrokesById((prev) => {
        const map = new Map(prev)
        const existing = map.get(key)

        if (!existing?.strokes?.length) return prev
        const updated = existing.strokes.slice(0, -1)
        const redoStack = [existing.strokes[existing.strokes.length - 1], ...(existing.redo || [])]

        updatedStrokes = updated
        map.set(key, { ...existing, strokes: updated, redo: redoStack })

        return map
      })
      requestAnimationFrame(() => {
        if (updatedStrokes !== null) redrawWithStrokes(updatedStrokes)
        else redrawCanvas()
      })

      return
    }

    if (appliedData?.appliedStrokes?.length > 0) {
      const updatedAppliedStrokes = appliedData.appliedStrokes.slice(0, -1)

      setAppliedStrokesById((prev) => {
        const map = new Map(prev)

        if (updatedAppliedStrokes.length === 0) map.delete(key)
        else map.set(key, { originalImageUrl: appliedData.originalImageUrl, appliedStrokes: updatedAppliedStrokes })

        return map
      })

      const baseImg = new Image()

      baseImg.crossOrigin = 'anonymous'
      baseImg.src = appliedData.originalImageUrl
      baseImg.onload = () => {
        const canvas = document.createElement('canvas')

        canvas.width = baseImg.naturalWidth || baseImg.width
        canvas.height = baseImg.naturalHeight || baseImg.height
        const ctx = canvas.getContext('2d')

        ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height)
        updatedAppliedStrokes.forEach((stroke) => drawStroke(ctx, stroke))

        if (updatedAppliedStrokes.length === 0) {
          setAttachments((prev) => {
            const next = [...prev]
            const oldPreviewUrl = active?.previewUrl

            next[activeIndex] = { ...active, previewUrl: appliedData.originalImageUrl }
            if (oldPreviewUrl && oldPreviewUrl !== appliedData.originalImageUrl) URL.revokeObjectURL(oldPreviewUrl)

            return next
          })

          return
        }

        canvas.toBlob(
          (blob) => {
            if (!blob) return
            const ext = active?.name?.split('.').pop() || 'png'
            const baseName = active?.name?.replace(/\.[^.]+$/, '') || 'annotated'
            const file = new File([blob], `${baseName}_edited.${ext}`, { type: blob.type || 'image/png' })
            const newPreviewUrl = URL.createObjectURL(blob)

            setAttachments((prev) => {
              const next = [...prev]
              const oldPreviewUrl = active?.previewUrl

              next[activeIndex] = { ...active, _undoKey: key, file, previewUrl: newPreviewUrl, name: file.name }

              if (oldPreviewUrl && oldPreviewUrl !== appliedData.originalImageUrl) URL.revokeObjectURL(oldPreviewUrl)

              return next
            })
          },
          'image/png',
          0.95
        )
      }
      baseImg.onerror = () => console.error('Failed to load original image for undo')
    }
  }

  const handleApplyAnnotation = () => {
    if (!isImage || !canvasRef.current || !imgElementRef.current) {
      stopAnnotating()

      return
    }
    if (active?.id) activeIdRef.current = active.id

    const offscreen = document.createElement('canvas')

    offscreen.width = canvasRef.current.width
    offscreen.height = canvasRef.current.height
    const ctx = offscreen.getContext('2d')

    ctx.drawImage(imgElementRef.current, 0, 0, offscreen.width, offscreen.height)
    getActiveStrokes().forEach((stroke) => drawStroke(ctx, stroke))

    const currentStrokes = getActiveStrokes()
    const key = active?.id || activeIdRef.current
    const originalImageUrl = active?.previewUrl

    offscreen.toBlob(
      (blob) => {
        if (!blob) {
          stopAnnotating()

          return
        }
        const ext = active?.name?.split('.').pop() || 'png'
        const baseName = active?.name?.replace(/\.[^.]+$/, '') || 'annotated'
        const file = new File([blob], `${baseName}_edited.${ext}`, { type: blob.type || 'image/png' })
        const newPreviewUrl = URL.createObjectURL(blob)

        if (key && currentStrokes.length > 0) {
          setAppliedStrokesById((prev) => {
            const map = new Map(prev)
            const existing = map.get(key) || { originalImageUrl, appliedStrokes: [] }

            map.set(key, {
              originalImageUrl: existing.originalImageUrl || originalImageUrl,
              appliedStrokes: [...(existing.appliedStrokes || []), ...currentStrokes]
            })

            return map
          })
        }

        setAttachments((prev) => {
          const next = [...prev]

          next[activeIndex] = {
            ...active,
            _undoKey: key,
            file,
            previewUrl: newPreviewUrl,
            name: file.name
          }

          return next
        })

        setStrokesById((prev) => {
          const map = new Map(prev)

          if (key && map.has(key)) map.delete(key)

          return map
        })

        stopAnnotating()
      },
      'image/png',
      0.95
    )
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])

    if (files.length && onAddAttachment) onAddAttachment(files)

    if (!files.length) return
    const mediaFiles = []
    const documentFiles = []

    files.forEach((file) => {
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        mediaFiles.push(file)
      } else {
        documentFiles.push(file)
      }
    })

    onAddAttachment?.(files)

    const parentIndex = attachments?.length ?? 0
    const newIndex = parentIndex + files.length - 1

    setActiveIndex(newIndex)
    if (mediaFiles.length) {
      handleMediaSelected?.(mediaFiles)
    }

    if (documentFiles.length) {
      // setActiveIndex(newIndex)
      handleDocumentSelected?.(documentFiles)
    }

    e.target.value = ''
  }

  const handleRemoveAttachment = (id) => {
    if (removeAttachment) removeAttachment(id)
    // adjust active index if necessary (will also be clamped in useEffect)
    if (attachments.length === 1) {
      setActiveIndex(0)
    } else {
      const idx = attachments.findIndex((a) => a.id === id)

      if (idx !== -1 && idx <= activeIndex && activeIndex > 0) {
        setActiveIndex((i) => Math.max(0, i - 1))
      }
    }
  }

  const handleCloseAll = () => {
    if (onCloseAll) return onCloseAll()
    // fallback: call removeAttachment for each
    attachments.forEach((a) => removeAttachment && removeAttachment(a.id))

    if (scrollToBottom) {
      setTimeout(() => {
        scrollToBottom()
      }, 0)
    }
  }

  const adjustTextareaHeight = (textarea) => {
    textMethods.autoResizeTextarea(textarea)
  }

  const updateCaptionLocal = (id, newCaption) => {
    // call parent updater if provided

    if (onUpdateAttachment) {
      onUpdateAttachment(id, { caption: newCaption })
    } else {
      // local fallback
      setLocalCaptions((prev) => {
        const next = new Map(prev)

        next.set(id, newCaption)

        return next
      })
    }

    // Adjust textarea height dynamically
    if (captionTextareaRef.current) {
      adjustTextareaHeight(captionTextareaRef.current)
    }
  }

  const getCaption = (id) => {
    if (onUpdateAttachment) {
      const att = attachments.find((a) => a.id === id)

      return att?.caption ?? ''
    }

    return localCaptions.get(id) ?? ''
  }

  // Close caption mention dropdown when clicking outside
  useEffect(() => {
    function handleDocMouseDown(e) {
      if (!showCaptionMentionDropdown || !captionRowRef.current) return
      const mentionDropdown = document.querySelector('.wa-caption-mention-dropdown')

      if (mentionDropdown && !captionRowRef.current.contains(e.target) && !mentionDropdown.contains(e.target)) {
        setShowCaptionMentionDropdown(false)
        setCaptionMentionSearchTerm('')
        setCaptionMentionStartPosition(null)
      }
    }

    document.addEventListener('mousedown', handleDocMouseDown)

    return () => document.removeEventListener('mousedown', handleDocMouseDown)
  }, [showCaptionMentionDropdown])

  // Reset caption mention state when active attachment changes
  useEffect(() => {
    setShowCaptionMentionDropdown(false)
    setCaptionMentionSearchTerm('')
    setCaptionSelectedMentionIndex(0)
    setCaptionMentionStartPosition(null)
  }, [active?.id])

  useEffect(() => {
    if (showCaptionMentionDropdown) setCaptionSelectedMentionIndex(0)
  }, [captionMentionSearchTerm, showCaptionMentionDropdown])

  const getFilteredCaptionMentionMembers = () => {
    const searchLower = (captionMentionSearchTerm || '').toLowerCase()
    const everyoneOption = { isEveryone: true, first_name: 'Everyone', last_name: '', userid: 'everyone', id: 'everyone' }
    const results = []

    if (!searchLower || 'everyone'.includes(searchLower)) results.push(everyoneOption)

    const filtered = (mentionMembers || []).filter((member) => {
      if (!captionMentionSearchTerm) return true
      const firstName = (member.first_name || '').toLowerCase()
      const lastName = (member.last_name || '').toLowerCase()
      const fullName = `${firstName} ${lastName}`.trim()

      return firstName.includes(searchLower) || lastName.includes(searchLower) || fullName.includes(searchLower)
    })

    results.push(...filtered)

    return { members: results, showNotFound: results.length === 0 }
  }

  const insertCaptionMention = (member) => {
    if (!active?.id || !member || captionMentionStartPosition === null) return

    const currentValue = getCaption(active.id)

    let mentionText

    if (member.isEveryone) {
      mentionText = '@Everyone'
    } else {
      const firstName = member.first_name || ''
      const lastName = member.last_name || ''
      const fullName = `${firstName} ${lastName}`.trim()

      // If no name available, don't insert a mention
      if (!fullName) return

      mentionText = `@${fullName}`
    }

    const textBeforeMention = currentValue.substring(0, captionMentionStartPosition)
    const textAfterAt = currentValue.substring(captionMentionStartPosition + 1)
    const mentionEndMatch = textAfterAt.match(REG_EX.MENTION_END_MATCH)
    const textToReplace = mentionEndMatch ? mentionEndMatch[0] : textAfterAt.split(/\s/)[0] || ''
    const textAfterMention = currentValue.substring(captionMentionStartPosition + 1 + textToReplace.length)
    const newValue = `${textBeforeMention}${mentionText} ${textAfterMention}`

    updateCaptionLocal(active.id, newValue)
    setShowCaptionMentionDropdown(false)
    setCaptionMentionSearchTerm('')
    setCaptionMentionStartPosition(null)
    setCaptionSelectedMentionIndex(0)

    requestAnimationFrame(() => {
      if (captionTextareaRef.current) {
        const newCursorPosition = textBeforeMention.length + mentionText.length + 1

        captionTextareaRef.current.focus()
        captionTextareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
        adjustTextareaHeight(captionTextareaRef.current)
      }
    })
  }

  const handleCaptionChange = (e) => {
    const newValue = e.target.value
    const id = active?.id

    if (id) updateCaptionLocal(id, newValue)

    if (!isInternalCommunity || !mentionMembers?.length) return

    const cursorPosition = e.target.selectionStart ?? newValue.length
    const textBeforeCursor = newValue.substring(0, cursorPosition)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)
      const charAfterAt = newValue.charAt(lastAtIndex + 1)
      const hasMultipleAt = charAfterAt === '@' || textAfterAt.startsWith('@')
      const hasInvalidChar = REG_EX.MENTION_INVALID_CHAR.test(textAfterAt) || hasMultipleAt

      if (!hasInvalidChar) {
        setCaptionMentionSearchTerm(textAfterAt.trim().toLowerCase())
        setCaptionMentionStartPosition(lastAtIndex)
        setShowCaptionMentionDropdown(true)
        setCaptionSelectedMentionIndex(0)
      } else {
        setShowCaptionMentionDropdown(false)
        setCaptionMentionSearchTerm('')
        setCaptionMentionStartPosition(null)
      }
    } else {
      setShowCaptionMentionDropdown(false)
      setCaptionMentionSearchTerm('')
      setCaptionMentionStartPosition(null)
    }
  }

  const handleCaptionKeyDown = (e) => {
    if (showCaptionMentionDropdown && isInternalCommunity && mentionMembers?.length > 0) {
      const { members: filteredMembers } = getFilteredCaptionMentionMembers()

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setCaptionSelectedMentionIndex((prev) => (prev < filteredMembers.length - 1 ? prev + 1 : prev))

        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setCaptionSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0))

        return
      }
      if (e.key === 'Enter' && filteredMembers.length > 0) {
        e.preventDefault()
        insertCaptionMention(filteredMembers[captionSelectedMentionIndex])

        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setShowCaptionMentionDropdown(false)
        setCaptionMentionSearchTerm('')
        setCaptionMentionStartPosition(null)
      }
    }
  }

  const handleCaptionPaste = (e) => {
    const clipboard = e.clipboardData

    if (!clipboard) return

    const imageFiles = Array.from(clipboard.files).filter((file) => file.type.startsWith('image/'))

    // If no images, allow normal paste behavior for text
    if (imageFiles.length === 0) return

    // Prevent default paste behavior for images
    e.preventDefault()

    // Important: clone file into new File object with unique naming
    const timestamp = Date.now()
    const randomId = Math.random().toString(36).slice(2, 8)

    const clonedFiles = imageFiles.map(
      (file, index) =>
        new File([file], `${timestamp}-${index}-${randomId}-${file.name}`, {
          type: file.type
        })
    )

    // Track how many images we're pasting so we can switch to the last one
    pendingPasteCountRef.current = clonedFiles.length

    // Add the pasted images using handleMediaSelected
    if (handleMediaSelected && clonedFiles.length > 0) {
      handleMediaSelected(clonedFiles)
    }

    // Focus will be maintained by the useEffect that handles the attachment switch
  }

  const handleSend = () => {
    if (onSendAttachments) return onSendAttachments()
    // fallback: log attachments and captions
    console.warn(
      'onSendAttachments prop not provided. attachments:',
      attachments.map((a) => ({ id: a.id, caption: getCaption(a.id) }))
    )
  }

  const handleCropClick = () => {
    if (isImage) {
      setCroppingIndex(activeIndex)
      setShowCropper(true)
    }
  }

  const handleCropComplete = (croppedBlob) => {
    if (croppingIndex === null) return

    const attachment = attachments[croppingIndex]

    if (!attachment) return

    // Create new file from blob
    const croppedFile = new File([croppedBlob], attachment.name, { type: attachment.mime || 'image/png' })

    // Create new preview URL
    const newPreviewUrl = URL.createObjectURL(croppedBlob)

    // Revoke old preview URL
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl)
    }

    // Update attachment in the array
    if (setAttachments) {
      setAttachments((prev) => {
        const updated = [...prev]
        const oldCaption = attachment.caption || ''

        // Replace the old attachment with the cropped one
        updated[croppingIndex] = {
          ...attachment,
          file: croppedFile,
          previewUrl: newPreviewUrl,
          caption: oldCaption,
          size: croppedBlob.size
        }

        return updated
      })
    } else if (addFilesProp) {
      // Fallback: use addFiles if setAttachments is not available
      const newAttachments = addFilesProp([croppedFile], 'media')

      if (newAttachments?.length && removeAttachment) {
        const oldCaption = attachment.caption || ''

        newAttachments[0].caption = oldCaption

        // Remove old and add new
        removeAttachment(attachment.id)
        if (handleMediaSelected) {
          handleMediaSelected([croppedFile])
        }
      }
    }

    setShowCropper(false)
    setCroppingIndex(null)
  }

  const handleCropCancel = () => {
    setShowCropper(false)
    setCroppingIndex(null)
  }

  // If there are no attachments, render minimal composer
  if (!attachments.length) {
    return (
      <div className="wa-compose-container">
        <div className="wa-input-bar">
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileSelect} />
          <button type="button" className="wa-input-plus" onClick={() => fileInputRef.current.click()}>
            +
          </button>
          <textarea className="wa-input" placeholder="Type a message..." value={''} onChange={() => {}} rows={1} />
          <button className="wa-send-btn" onClick={handleSend} aria-label="Send">
            📤
          </button>
        </div>
      </div>
    )
  }

  const renderThumb = (att, thumbIsImage, thumbIsVideo) => {
    if (thumbIsImage) return <img src={att.previewUrl} alt={att.name} />
    if (thumbIsVideo) return <video src={att.previewUrl} muted />

    return <div className="wa-doc-thumb">📄</div>
  }

  const renderBigPreview = () => {
    // Show cropper if active
    if (showCropper && isImage) {
      return <ImageCropper imageSrc={active.previewUrl} onCrop={handleCropComplete} onCancel={handleCropCancel} />
    }

    if (isAnnotating && isImage) {
      return (
        <div className="wa-annotator">
          <canvas
            ref={canvasRef}
            className="wa-annotator-canvas"
            onMouseDown={handleMouseDown}
            // pointer events handled globally for move/up
          />
        </div>
      )
    }

    if (isImage) {
      return <img src={active.previewUrl} alt={active?.name} className="wa-big-media" />
    }

    if (isVideo) {
      return <video src={active.previewUrl} className="wa-big-media" controls playsInline muted />
    }

    return (
      <div className="wa-big-doc">
        <div className="wa-doc-icon">📄</div>
        <div className="wa-doc-name">{active?.name}</div>
      </div>
    )
  }

  return (
    <div className="wa-compose-container wa-whatsapp-style">
      {/* big centered preview */}
      <div className="wa-big-preview-wrap">
        <div className="wa-big-preview">
          {/* {isImage ? (
            <img src={active.previewUrl} alt={active?.name} className="wa-big-media" />
          ) : isVideo ? (
            <video src={active.previewUrl} className="wa-big-media" controls playsInline muted />
          ) : (
            <div className="wa-big-doc">
              <div className="wa-doc-icon">📄</div>
              <div className="wa-doc-name">{active?.name}</div>
            </div>
          )} */}
          {renderBigPreview()}
        </div>

        {/* Commented for current PR */}
        {/* {isAnnotating && isImage && (
          <div className="wa-preview-actions-left">
            <Button className="wa-action-btn" icon="pi pi-undo" onClick={handleUndo} disabled={!getActiveStrokes().length} aria-label="Undo annotation" />
            <Button className="wa-action-btn redo-btn" icon="pi pi-undo" onClick={handleRedo} disabled={!getActiveRedo().length} aria-label="Redo annotation" />
          </div>
        )} */}

        <div className="wa-preview-actions">
          {!showCropper && isImage && !isAnnotating && (
            <button className="wa-crop-btn" onClick={handleCropClick} aria-label="Crop image">
              <svg className="crop-rotate" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#fff">
                <path d="M480 0q-94 0-177.5-33.5t-148-93Q90-186 49-266.5T0-440h80q8 72 38.5 134.5t79 110.5Q246-147 309-117.5T444-82l-62-62 56-56L618-20Q584-9 549.5-4.5T480 0Zm120-200v-80H360q-33 0-56.5-23.5T280-360v-240h-80v-80h80v-80h80v400h400v80h-80v80h-80Zm0-240v-160H440v-80h160q33 0 56.5 23.5T680-600v160h-80Zm280-80q-7-72-38-134.5T762.5-765Q714-813 651-842.5T516-878l62 62-56 56-180-180q34-11 68.5-15.5T480-960q94 0 177.5 33.5t148 93Q870-774 911-693.5T960-520h-80Z" />
              </svg>
            </button>
          )}
          {isImage && !isAnnotating && !showCropper && <Button className="wa-edit-btn" icon="pi pi-pencil" onClick={startAnnotating} aria-label="Annotate image" />}
          {isImage && !showCropper && isAnnotating && (
            <button className="wa-undo-btn" onClick={handleUndo} aria-label="Undo annotation">
              <i className="pi pi-undo"></i>
            </button>
          )}

          <button className="wa-close-all" onClick={handleCloseAll} aria-label="Close attachments">
            ✕
          </button>
        </div>

        {/* caption / toolbar row - hide during crop */}
        {!showCropper && (
          <div className="wa-caption-row" ref={captionRowRef}>
            {isAnnotating && isImage ? (
              <div className="wa-annotator-toolbar inline">
                <div className="wa-annotator-colors">
                  {COLORS.map((c) => (
                    <button key={c} className={`wa-color-swatch ${brushColor === c ? 'active' : ''}`} style={{ backgroundColor: c }} onClick={() => setBrushColor(c)} aria-label={`Set brush color ${c}`} />
                  ))}
                </div>

                <div className="wa-annotator-size">
                  <span className="wa-size-label">Brush</span>
                  <input type="range" min="2" max="16" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} />
                  <span className="wa-size-value">{brushSize}px</span>
                </div>

                <div className="wa-annotator-actions">
                  <Button className="annotations-cancel-btn" onClick={handleCancelAnnotate}>
                    Cancel
                  </Button>
                  <Button className="annotations-apply-btn" onClick={handleApplyAnnotation} disabled={isUploadingImage}>
                    Apply
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <textarea ref={captionTextareaRef} className="wa-caption-input" placeholder="Add a caption..." rows={2} value={getCaption(active?.id)} onChange={handleCaptionChange} onKeyDown={handleCaptionKeyDown} onPaste={handleCaptionPaste} />
                {showCaptionMentionDropdown &&
                  isInternalCommunity &&
                  mentionMembers?.length > 0 &&
                  (() => {
                    const { members: filteredMembers } = getFilteredCaptionMentionMembers()

                    if (filteredMembers.length === 0) return null

                    return <MentionDropdown members={filteredMembers} selectedIndex={captionSelectedMentionIndex} onSelect={insertCaptionMention} onHover={setCaptionSelectedMentionIndex} className="wa-caption-mention-dropdown" />
                  })()}
              </>
            )}
          </div>
        )}
      </div>

      {/* bottom thumbnail strip with add + thumbnails + send on right */}
      <div className="wa-thumb-bottom">
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple onChange={handleFileSelect} />

        <div className="wa-thumb-left">
          <div className="wa-thumbs" role="list">
            {attachments.map((att, idx) => {
              const thumbIsImage = att.mime?.startsWith?.('image/')
              const thumbIsVideo = att.mime?.startsWith?.('video/')

              return (
                <div key={att.id} className={`wa-thumb-item ${idx === activeIndex ? 'active' : ''}`} onClick={() => setActiveIndex(idx)} title={att.name} role="listitem">
                  <div className="wa-thumb-media">{renderThumb(att, thumbIsImage, thumbIsVideo)}</div>
                  <button
                    className="wa-thumb-remove"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveAttachment(att.id)
                    }}
                    aria-label={`Remove ${att.name}`}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              )
            })}
          </div>

          <button type="button" className="wa-thumb-add" onClick={() => fileInputRef.current.click()} title="Add more" aria-label="Add attachments">
            +
          </button>
        </div>

        <div className="wa-thumb-send">
          <Button rounded icon={isUploadingImage ? 'pi pi-spin pi-spinner' : 'pi pi-send send-icon'} className={`send-button ${attachments.length > 0 ? 'has-attachments' : ''}`} onClick={handleSend} disabled={isUploadingImage} />
        </div>
      </div>
    </div>
  )
}
